import { useState, useEffect, useRef } from 'react';
import { Pane, Text, Table, SelectField, Button, TextInputField, Heading, toaster, IconButton, RemoveIcon, Card } from 'evergreen-ui';
import { getVolunteerAttendances, markVolunteerAttendance, removeVolunteerAttendance } from '../services/volunteersServices';
import useWindowSize from '../utils/useWindowSize';
import '../styles/responsive.css';

const VolunteerAttendance = ({ volunteers }) => {
    const [attendances, setAttendances] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedVolId, setSelectedVolId] = useState('');
    const [orarioIngresso, setOrarioIngresso] = useState('');
    const [orarioUscita, setOrarioUscita] = useState('');

    const width = useWindowSize();
    const isMobile = width < 920;
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        fetchAttendances(selectedDate);
        return () => { mountedRef.current = false; };
    }, [selectedDate]);

    const fetchAttendances = async (date) => {
        try {
            const data = await getVolunteerAttendances(date);
            if (mountedRef.current) setAttendances(data || []);
        } catch (error) {
            console.error('Errore:', error);
        }
    };

    const handleMarkAttendance = async () => {
        if (!selectedVolId || !orarioIngresso) {
            toaster.warning('Inserire volontario e orario presenza');
            return;
        }

        try {
            const result = await markVolunteerAttendance({
                volunteer_id: selectedVolId,
                data_presenza: selectedDate,
                orario_ingresso: orarioIngresso,
                orario_uscita: orarioUscita || null
            });
            if (result && result.success) {
                toaster.success('Presenza segnata');
                fetchAttendances(selectedDate);
                setSelectedVolId('');
                setOrarioIngresso('');
                setOrarioUscita('');
            } else {
                toaster.danger('Errore');
            }
        } catch (error) {
            console.error(error);
            toaster.danger('Errore');
        }
    };

    const handleRemoveAttendance = async (id) => {
        try {
            const result = await removeVolunteerAttendance(id);
            if (result && result.success) {
                toaster.success('Presenza rimossa');
                fetchAttendances(selectedDate);
            }
        } catch (error) {
            toaster.danger('Errore');
        }
    };

    return (
        <Pane>
            <Heading size={600} marginBottom={16}>Gestione presenze volontari</Heading>
            <Pane display="flex" gap={16} marginBottom={16}>
                <TextInputField
                    type="date"
                    label="Data di presenza"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                />
            </Pane>

            {isMobile ? (
                <Pane>
                    {(!Array.isArray(attendances) || attendances.length === 0) ? (
                        <Text>Nessuna presenza trovata per questa data.</Text>
                    ) : (
                        <div className="responsive-list">
                            {attendances.map(a => (
                                <Card key={`att-${a.id}`} className="responsive-card" background="tint1">
                                    <div className="field-row">
                                        <div className="field-label">Volontario</div>
                                        <div className="field-value">{a.nome} {a.cognome}</div>
                                    </div>
                                    <div className="field-row">
                                        <div className="field-label">Ingresso</div>
                                        <div className="field-value">{a.orario_ingresso}</div>
                                    </div>
                                    <div className="field-row">
                                        <div className="field-label">Uscita</div>
                                        <div className="field-value">{a.orario_uscita || '-'}</div>
                                    </div>
                                    <Pane display="flex" justifyContent="flex-end" marginTop={16}>
                                        <Button iconBefore={RemoveIcon} intent="danger" onClick={() => handleRemoveAttendance(a.id)}>Rimuovi</Button>
                                    </Pane>
                                </Card>
                            ))}
                        </div>
                    )}
                </Pane>
            ) : (
                <Table>
                    <Table.Head>
                        <Table.TextHeaderCell>Volontario</Table.TextHeaderCell>
                        <Table.TextHeaderCell>Inizio</Table.TextHeaderCell>
                        <Table.TextHeaderCell>Fine</Table.TextHeaderCell>
                        <Table.TextHeaderCell flexBasis={80} flexShrink={0} flexGrow={0}>Rimuovi</Table.TextHeaderCell>
                    </Table.Head>
                    <Table.Body height="auto">
                        {(!Array.isArray(attendances) || attendances.length === 0) ? (
                            <Pane display="flex" alignItems="center" justifyContent="center" height={60}>
                                <Text>Nessuna presenza trovata per questa data.</Text>
                            </Pane>
                        ) : (
                            attendances.map(a => (
                                <Table.Row key={a.id}>
                                    <Table.TextCell>{a.nome} {a.cognome}</Table.TextCell>
                                    <Table.TextCell>{a.orario_ingresso}</Table.TextCell>
                                    <Table.TextCell>{a.orario_uscita || '-'}</Table.TextCell>
                                    <Table.Cell flexBasis={80} flexShrink={0} flexGrow={0}>
                                        <IconButton icon={RemoveIcon} intent="danger" appearance="minimal" onClick={() => handleRemoveAttendance(a.id)} />
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        )}
                    </Table.Body>
                </Table>
            )}

            <Pane marginTop={24} display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={16} alignItems={isMobile ? 'stretch' : 'flex-end'}>
                <SelectField
                    label="Seleziona Volontario"
                    value={selectedVolId}
                    onChange={e => setSelectedVolId(e.target.value)}
                    marginBottom={0}
                >
                    <option value="">-- Seleziona --</option>
                    {Array.isArray(volunteers) && volunteers.map(v => (
                        <option value={v.id} key={v.id}>{v.nome} {v.cognome}</option>
                    ))}
                </SelectField>
                <TextInputField
                    type="time"
                    label="Ora Ingresso"
                    value={orarioIngresso}
                    onChange={e => setOrarioIngresso(e.target.value)}
                    marginBottom={0}
                />
                <TextInputField
                    type="time"
                    label="Ora Uscita"
                    value={orarioUscita}
                    onChange={e => setOrarioUscita(e.target.value)}
                    marginBottom={0}
                />
                <Button appearance="primary" intent="success" onClick={handleMarkAttendance} marginBottom={0} width={isMobile ? '100%' : 'auto'} marginTop={isMobile ? 16 : 0}>
                    Segna presenza
                </Button>
            </Pane>
        </Pane>
    );
};

export default VolunteerAttendance;
